import React, {
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from "react";
import { useSearchParams } from "react-router-dom";

import { SATOSContentRender } from "@/app/application/SATOSContentRender";
import { useTime } from "@/app/application/TimeContext";
import { Translate, GetLanguageValueAsString } from "@/app/application/Translate";
import { UserContext, TranslationContext } from "@/app/providers";
import { stringAvatar } from "@/features/procedures/lib/checklist";
import { useShiftPage } from "@/features/shift/hooks";
import {
	type Comment as ShiftComment,
	type ShiftEventResponse,
} from "@/features/shift/model/Shift/Shift";
import {
	MobileShiftEventCards,
	ShiftEvent,
	ShiftMetaBadge,
	ShiftSummaryDialogue,
	useShiftHeaderActions,
} from "@/features/shift/ui/components";
import { COL } from "@/features/task";
import {
	type AnyCol,
	CustomToolbarV2,
	PdfExportDialog,
	type PdfExportDialogState,
	type PdfMeta,
	StyledDataGrid,
	buildPdfShowAllInfo,
	exportArchiveGridToExcel,
	headerOf,
} from "@/shared/data-grid";
import { isImageAttachment } from "@/shared/lib/attachmentPreview";
import { AlertRequest } from "@/shared/notifications/AlertRequest";
import { TableContainer } from "@/shared/ui";
import { DataGridCommentTooltip } from "@/shared/ui/DataGridCommentTooltip";
import { ImageAttachmentPreviewDialog } from "@/shared/ui/ImageAttachmentPreviewDialog";
import { useShiftColumnVisibility } from "@/storage/useShiftColumnVisibility";
import {
	sharedShiftDataGridShellSx,
	sharedTableCheckboxSx,
	sharedTableTagChipSx,
} from "@/styles/sharedDataGridStyles";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import FilterAltRoundedIcon from "@mui/icons-material/FilterAltRounded";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import TableChartIcon from "@mui/icons-material/TableChart";
import TableViewOutlinedIcon from "@mui/icons-material/TableViewOutlined";
import ViewWeekRoundedIcon from "@mui/icons-material/ViewWeekRounded";
import {
	Avatar,
	Box,
	Button,
	Card,
	Checkbox,
	Chip,
	CircularProgress,
	ListItemIcon,
	ListItemText,
	Link as MLink,
	MenuItem,
	TablePagination,
	Tooltip,
	Typography,
	alpha,
	styled,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import type { Theme } from "@mui/material/styles";
import {
	GridCellParams,
	GridColDef,
	GridColumnVisibilityModel,
	GridRowClassNameParams,
	useGridApiRef,
} from "@mui/x-data-grid";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import rehypeRaw from "rehype-raw";

import logoSvg from "@/assets/svg/logo/logo-satos-red.svg?url";

import { useFetchApplicationConfiguration } from "@/service/ApplicationQueries";
import {
	useFetchShift,
	useFetchShiftEvent,
	useFetchShiftOptions,
} from "@/service/ShiftHomeQueries";
import {
	useMutateShiftEventsSaveClear,
	useMutateUpdateShiftEventStatus,
} from "@/service/ShiftMutations";
import { Language } from "@/model/Language/Language";


/* dayjs tz support (required for .tz(timezone)) */
dayjs.extend(utc);
dayjs.extend(timezone);

/** Debounce a value for server-side search */
function useDebounced(value: string, delay = 300): string {
	const [debounced, setDebounced] = useState(value);
	useEffect(() => {
		const t = setTimeout(() => setDebounced(value), delay);
		return () => clearTimeout(t);
	}, [value, delay]);
	return debounced;
}

type ShiftLogRow = ShiftEventResponse & {
	Crew?: unknown;
	Group?: unknown;
	GroupId?: number | string | null;
	groupId?: number | string | null;
	System?: unknown;
	SystemId?: number | string | null;
	systemId?: number | string | null;
	ShiftCrewId?: number | string | null;
	DateRaw: ShiftEventResponse["Date"] | null;
	DateText: string;
	LoggedByText: string;
	DescriptionPlain: string;
	ShiftName: string;
	TagsFlat: string;
	GroupText: string;
	CommentsPlain: string;
	SystemName: string;
	AttachmentText: string;
	IsOutOfServiceBool: boolean;
	IsHandoverEventBool: boolean;
	IsOutOfServiceText: string;
	IsHandoverEventText: string;
	ShiftCrewText: string;
};

type ShiftLogColDef = GridColDef<ShiftLogRow>;

const isRecord = (value: unknown): value is Record<string, unknown> =>
	typeof value === "object" && value !== null;

const toText = (value: unknown): string => {
	if (typeof value === "string") return value.trim();
	if (typeof value === "number") return String(value);
	return "";
};

const toEntityLabel = (value: unknown): string => {
	if (isRecord(value)) {
		const name = toText(value["Name"] ?? value["name"]);
		if (name) return name;

		const id = toText(value["Id"] ?? value["id"]);
		if (id) return id;
		return "";
	}

	return toText(value);
};

const toEntityLabels = (value: unknown): string[] => {
	if (!Array.isArray(value)) return [];

	return value
		.map((entry) => toEntityLabel(entry))
		.filter((entry): entry is string => Boolean(entry));
};

const toBooleanFlag = (value: unknown): boolean =>
	value === true || value === 1 || value === "1";

const resolveEventId = (event: ShiftEventResponse): number | undefined => {
	if (typeof event.Id === "number") return event.Id;
	if (typeof event.id === "number") return event.id;
	return undefined;
};

/* ─────────────────── Styled bits ─────────────────── */
const bandBg = (t: Theme) =>
	t.palette.mode === "light" ? "#F8FAFC" : t.palette.background.default;

export const StyledButton = styled(Button)(() => ({
	height: 40,
}));

export const CustomNoRowsOverlay = React.memo(() => (
	<Box
		sx={{
			display: "flex",
			justifyContent: "center",
			alignItems: "center",
			height: "100%",
			textAlign: "center",
			width: "100%",
			bgcolor: "background.default",
			color: "text.secondary",
		}}
	>
		<Typography variant="body1">
			<Translate String="ShiftLog.NoItemsAvailable">
				No items available
			</Translate>
		</Typography>
	</Box>
));
CustomNoRowsOverlay.displayName = "CustomNoRowsOverlay";

/* ─────────────────── Page ─────────────────── */
export const ShiftLog = () => {
	const translation: Language | null = useContext(TranslationContext);
	const [searchParams, setSearchParams] = useSearchParams();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
	const apiRef = useGridApiRef();
	const user = useContext(UserContext);
	const { searchInput, selectedGroup, setSelectedGroup, groupIdOrUndefined } =
		useShiftPage();
	const setHeaderActions = useShiftHeaderActions();

	const [columnVisibilityModel, setColumnVisibilityModel] =
		useShiftColumnVisibility();

	const [openShiftEvent, setOpenShiftEvent] = useState(false);
	const [openSummarizeDialogue, setOpenSummarizeDialogue] = useState(false);
	const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
	const [isPdfExporting, setIsPdfExporting] = useState(false);
	const [isExcelExporting, setIsExcelExporting] = useState(false);
	const [selectedPdfFields, setSelectedPdfFields] = useState<Set<string>>(
		() => new Set(),
	);
	const [imageAttachmentPreview, setImageAttachmentPreview] = useState<{
		url: string;
		fileName: string;
	} | null>(null);
	const [pdfExportOpts, setPdfExportOpts] = useState<PdfExportDialogState>({
		dateFormat: "DD/MM/YY HH:mm:ss",
		footerText: "Satos",
		truncateAt: 200,
		truncateEnabled: false,
		pageSize: "a4",
		groupBy: "",
	});

	const [shiftEventData, setShiftEventData] =
		useState<ShiftEventResponse | null>(null);

	const [page, setPage] = useState<number>(1);
	const [pageSize, setPageSize] = useState<number>(10);
	const debouncedSearch = useDebounced(searchInput, 350);
	const rawEventId = searchParams.get("eventId");
	const urlEventId =
		rawEventId && /^\d+$/.test(rawEventId) ? Number(rawEventId) : undefined;

	const { data: shiftOptions, isLoading: optionsLoading } =
		useFetchShiftOptions();
	const { data: appConfig, isLoading: loadingAppConfig } =
		useFetchApplicationConfiguration();

	const shiftQuery = useFetchShift({
		GroupId: groupIdOrUndefined,
		IsFromShiftStart: true,
		Page: page,
		PageSize: pageSize,
		Search: debouncedSearch.trim() || undefined,
	});
	const shiftGroupEvent = shiftQuery.data;
	const shiftLogLoading = shiftQuery.isLoading;
	const { data: urlShiftEventData } = useFetchShiftEvent(urlEventId);

	const { updateShiftEventStatus, isUpdating } =
		useMutateUpdateShiftEventStatus();
	const { updateGroupSaveClear, isUpdating: isUpdatingSaveClear } =
		useMutateShiftEventsSaveClear("ShiftLog");

	/* derive rows + paging */
	const rows = useMemo<ShiftEventResponse[]>(() => {
		const items = Array.isArray(shiftGroupEvent?.Items)
			? shiftGroupEvent.Items
			: Array.isArray(shiftGroupEvent?.items)
				? shiftGroupEvent.items
				: [];
		// newest first
		return items
			.slice()
			.sort(
				(a, b) =>
					dayjs(b.Date ?? b.date).unix() - dayjs(a.Date ?? a.date).unix(),
			);
	}, [shiftGroupEvent]);

	const totalItems = shiftGroupEvent?.Total ?? shiftGroupEvent?.total ?? 0;
	const exportFileName = useMemo(
		() => `Shift_Log_${dayjs().format("YYYY-MM-DD_HH-mm")}`,
		[],
	);

	/* ensure selectedGroup is one of user's groups */
	useEffect(() => {
		if (!shiftOptions?.Groups?.length || !user) return;

		const userGroupIds = new Set((user?.Groups ?? []).map((g) => String(g.Id)));
		const allowed = (shiftOptions?.Groups ?? []).filter((g) =>
			userGroupIds.has(String(g.Id)),
		);

		const isValid =
			selectedGroup === "all" ||
			allowed.some((g) => String(g.Id) === selectedGroup);

		if (!isValid && allowed[0]) {
			setSelectedGroup(String(allowed[0].Id));
		}
	}, [shiftOptions, user, setSelectedGroup]);

	useEffect(() => setPage(1), [searchInput, selectedGroup]);

	useEffect(() => {
		if (!urlEventId) {
			setOpenShiftEvent(false);
			return;
		}

		const matchingRow = rows.find((row) => resolveEventId(row) === urlEventId);
		if (matchingRow) {
			setShiftEventData(matchingRow);
			setOpenShiftEvent(true);
		}
	}, [rows, urlEventId]);

	useEffect(() => {
		if (!urlEventId || Number(urlShiftEventData?.Id) !== urlEventId) return;

		setShiftEventData(urlShiftEventData);
		setOpenShiftEvent(true);
	}, [urlEventId, urlShiftEventData]);

	const updateEventIdInUrl = useCallback(
		(eventId?: number, replace = false) => {
			setSearchParams(
				(prev) => {
					const next = new URLSearchParams(prev);

					if (typeof eventId === "number" && Number.isFinite(eventId)) {
						next.set("eventId", String(eventId));
					} else {
						next.delete("eventId");
					}

					return next;
				},
				{ replace },
			);
		},
		[setSearchParams],
	);

	const handleCloseShiftEvent = useCallback(() => {
		setOpenShiftEvent(false);
		updateEventIdInUrl(undefined, true);
	}, [updateEventIdInUrl]);

	const handleClickOpenShiftEvent = useCallback(
		(row: ShiftLogRow) => {
			setShiftEventData(row);
			setOpenShiftEvent(true);
			updateEventIdInUrl(resolveEventId(row));
		},
		[updateEventIdInUrl],
	);

	const handleCellClick = useCallback(
		(params: GridCellParams<ShiftLogRow>) =>
			handleClickOpenShiftEvent(params.row),
		[handleClickOpenShiftEvent],
	);

	const handleRowsPerPageChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
			const newPageSize = parseInt(
				(event.target as HTMLInputElement).value,
				10,
			);
			setPageSize(newPageSize);
			setPage(1);
		},
		[],
	);

	const handleUpdateShiftEventStatus = (row: ShiftLogRow): ShiftLogRow => {
		try {
			const shiftId = resolveEventId(row);
			if (typeof shiftId !== "number") return row;

			const request = {
				shiftId,
				isHandoverEvent: toBooleanFlag(
					row.IsHandoverEvent ?? row.isHandoverEvent,
				),
				isOutOfService: toBooleanFlag(row.IsOutOfService ?? row.isOutOfService),
			};
			AlertRequest.genericConfirmation(updateShiftEventStatus, request);
			return row;
		} catch (e) {
			console.error(e);
			return row; // keep grid consistent
		}
	};

	const processRowUpdate = (newRow: ShiftLogRow): ShiftLogRow => {
		const res = handleUpdateShiftEventStatus(newRow);
		return res ?? newRow;
	};

	const handleSaveAndClear = useCallback(
		(groupId?: number) => {
			if (!groupId) return;
			try {
				const request = { groupId };
				AlertRequest.genericConfirmation(updateGroupSaveClear, request);
			} catch (e) {
				console.error(e);
			}
		},
		[updateGroupSaveClear],
	);

	const handleOpenSummarizeDialogue = useCallback(
		() => setOpenSummarizeDialogue(true),
		[],
	);

	const togglePreferencePanel = useCallback(
		(panel: "columns" | "filters") => {
			const api = apiRef.current;
			if (!api) return;

			const preferencePanel = (
				api.state as {
					preferencePanel?: {
						open?: boolean;
						openedPanelValue?: string;
					};
				}
			).preferencePanel;
			if (
				preferencePanel?.open &&
				preferencePanel?.openedPanelValue === panel
			) {
				api.hidePreferences?.();
				return;
			}

			api.showPreferences?.(panel);
		},
		[apiRef],
	);

	const handleOpenColumnsPanel = useCallback(() => {
		if (isMobile) return;
		togglePreferencePanel("columns");
	}, [isMobile, togglePreferencePanel]);

	const handleOpenFilterPanel = useCallback(() => {
		if (isMobile) return;
		togglePreferencePanel("filters");
	}, [isMobile, togglePreferencePanel]);

	const handleExportCsv = useCallback(() => {
		apiRef.current?.exportDataAsCsv?.({
			fileName: exportFileName,
			utf8WithBom: true,
			delimiter: ",",
			includeHeaders: true,
		});
	}, [apiRef, exportFileName]);

	const handleExportExcel = useCallback(async () => {
		if (isExcelExporting) return;
		setIsExcelExporting(true);
		try {
			await exportArchiveGridToExcel(apiRef, { fileName: exportFileName });
		} finally {
			setIsExcelExporting(false);
		}
	}, [apiRef, exportFileName, isExcelExporting]);

	const { timezone } = useTime();

	// ───────────────── helpers: CSV/XLSX friendly ─────────────────
	const stripHtml = (s?: string) =>
		(s || "")
			.replace(/<[^>]*>/g, " ")
			.replace(/\s+/g, " ")
			.trim();

	const fmt = (d?: ShiftLogRow["DateRaw"]) =>
		d ? dayjs.utc(d).tz(timezone).format("DD/MM/YY HH:mm:ss") : "";

	// shape rows into flat exportable fields; but feed the grid with these so valueGetter/valueFormatter match export
	const exportableRows = useMemo<ShiftLogRow[]>(
		() =>
			rows.map((r) => {
				const crewObj = r.ShiftCrew ?? r.shiftCrew ?? r["Crew"] ?? null;
				const crewName =
					toEntityLabel(crewObj) ||
					toText(r["ShiftCrewText"]) ||
					toText(r["ShiftCrewId"] ?? r["shiftCrewId"]);

				const groupsArr = toEntityLabels(r.Groups ?? r.groups ?? r["Groups"]);
				const groupId = r["GroupId"] ?? r["groupId"];
				const groupText =
					(groupsArr.length ? groupsArr.join(", ") : "") ||
					toText(r["GroupText"]) ||
					toEntityLabel(r["Group"]) ||
					(shiftOptions?.Groups?.find((x) => String(x.Id) === String(groupId))
						?.Name ??
						"") ||
					toText(groupId);

				const systemsArr = toEntityLabels(
					r.Systems ?? r.systems ?? r["Systems"],
				);
				const systemId = r["SystemId"] ?? r["systemId"];
				const systemName =
					(systemsArr.length ? systemsArr.join(", ") : "") ||
					toText(r["SystemName"]) ||
					toEntityLabel(r["System"]) ||
					(shiftOptions?.Systems?.find((x) => String(x.Id) === String(systemId))
						?.Name ??
						"") ||
					toText(systemId);

				const comments: ShiftComment[] = Array.isArray(r.Comments)
					? r.Comments
					: Array.isArray(r.comments)
						? r.comments
						: [];
				const commentsPlain = comments
					.map((comment) => {
						const text = stripHtml(comment.Text ?? comment.text ?? "");
						const created = comment.Created ?? comment.created ?? null;
						return `${text} (${fmt(created)})`;
					})
					.join(" | ");

				const dateRaw = r.Date ?? r.date ?? null;
				const loggedByText = toText(r.LoggedBy ?? r.loggedBy);
				const description = toText(r.Description ?? r.description);
				const shiftName =
					toText(r.DayNight?.Name ?? r.dayNight?.Name) ||
					toEntityLabel(r["Shift"] ?? r["shift"]);
				const tags = Array.isArray(r.Tags)
					? r.Tags
					: Array.isArray(r.tags)
						? r.tags
						: [];
				const isOutOfService = toBooleanFlag(
					r.IsOutOfService ?? r.isOutOfService,
				);
				const isHandoverEvent = toBooleanFlag(
					r.IsHandoverEvent ?? r.isHandoverEvent,
				);
				const filename = toText(r.Filename ?? r.filename);
				const filepath = toText(r.Filepath ?? r.filepath);

				return {
					...r,
					DateRaw: dateRaw,
					DateText: fmt(dateRaw),
					LoggedByText: loggedByText,
					DescriptionPlain: stripHtml(description),
					ShiftName: shiftName,
					TagsFlat: tags.join(", "),
					GroupText: groupText,
					CommentsPlain: commentsPlain,
					SystemName: systemName,
					AttachmentText: filename || filepath,
					IsOutOfServiceBool: isOutOfService,
					IsHandoverEventBool: isHandoverEvent,
					IsOutOfServiceText: isOutOfService ? "Yes" : "No",
					IsHandoverEventText: isHandoverEvent ? "Yes" : "No",
					ShiftCrewText: crewName,
				};
			}),
		[rows, shiftOptions, timezone],
	);

	// ───────────────── columns ─────────────────

	const commonDateCol: ShiftLogColDef = useMemo(
		() => ({
			field: "DateText",
			headerName: "Date",
			flex: 1,
			minWidth: 140,
			headerAlign: "left",
			align: "left",
			sortable: true,
			renderHeader: () => <Translate String="Shift.Event.Date">Date</Translate>,
			renderCell: ({ row }) => {
				if (!row.DateRaw) {
					return <Typography variant="body2">—</Typography>;
				}

				const localDate = dayjs.utc(row.DateRaw).tz(timezone);
				const dayDiff = dayjs()
					.startOf("day")
					.diff(localDate.startOf("day"), "day");
				const relativeLabel =
					dayDiff === 0
						? "Today"
						: dayDiff === 1
							? "Yesterday"
							: `${Math.abs(dayDiff)}d ${dayDiff > 0 ? "ago" : "ahead"}`;

				return (
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							justifyContent: "center",
							minWidth: 0,
							width: "100%",
							height: "100%",
							py: 0.25,
							textAlign: "left",
						}}
					>
						<Typography
							variant="caption"
							fontWeight={700}
							sx={{
								fontVariantNumeric: "tabular-nums",
								lineHeight: 1.15,
								letterSpacing: 0.1,
							}}
						>
							{localDate.format("DD/MM/YY HH:mm")}
						</Typography>
						<Box
							sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.2, flexWrap: "wrap" }}
						>
							<Typography variant="caption" color="text.secondary" noWrap>
								{relativeLabel}
							</Typography>
							{row.IsHandoverEventBool ? (
								<Typography variant="caption" sx={{ fontWeight: 600, color: "success.dark" }}>
									Handover
								</Typography>
							) : null}
							{row.IsOutOfServiceBool ? (
								<Typography variant="caption" sx={{ fontWeight: 600, color: "warning.dark" }}>
									OOS
								</Typography>
							) : null}
						</Box>
					</Box>
				);
			},
		}),
		[theme, timezone],
	);

	const mobileColumns: ShiftLogColDef[] = useMemo(
		() => [
			commonDateCol,
			{
				field: "LoggedByText",
				headerName: "Author",
				flex: 1,
				minWidth: 100,
				sortable: true,
				renderHeader: () => (
					<Translate String="Shift.Event.Author">Author</Translate>
				),
				renderCell: ({ row }) => (
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							gap: 0.75,
							minWidth: 0,
						}}
					>
						{row.LoggedByText ? (
							<Avatar
								{...stringAvatar(row.LoggedByText)}
								sx={(t) => ({
									width: 28,
									height: 28,
									fontSize: t.typography.caption.fontSize,
									flexShrink: 0,
								})}
							/>
						) : null}
						<Box sx={{ minWidth: 0 }}>
							<Typography variant="body2" fontWeight={700} noWrap>
								{row.LoggedByText || "—"}
							</Typography>
						</Box>
					</Box>
				),
			},
			{
				field: "DescriptionPlain",
				headerName: "Description",
				flex: 3,
				minWidth: 180,
				sortable: true,
				renderHeader: () => (
					<Translate String="Shift.Event.Description">Description</Translate>
				),
				renderCell: ({ row }) => (
					<Tooltip
						arrow
						title={
							<Box
								sx={(t) => ({
									maxWidth: 520,
									fontSize: t.typography.body2.fontSize,
									lineHeight: 1.5,
									"& p": { margin: 0, marginBottom: 0.5 },
									"& p:last-child": { marginBottom: 0 },
									"& img": {
										display: "block",
										width: "100%",
										height: 220,
										objectFit: "contain",
										borderRadius: 1,
									},
									"& iframe, & video": {
										width: "100%",
										height: 220,
										objectFit: "contain",
									},
								})}
							>
								<SATOSContentRender
									isNotProcedureRender={true}
									rehypePlugins={[rehypeRaw]}
									content={toText(row.Description ?? row.description)}
								/>
							</Box>
						}
					>
						<Box sx={{ width: "100%", minWidth: 0 }}>
							<Typography
								variant="body2"
								sx={{
									display: "-webkit-box",
									WebkitLineClamp: 3,
									WebkitBoxOrient: "vertical",
									overflow: "hidden",
									lineHeight: 1.45,
								}}
							>
								{row.DescriptionPlain || "No description"}
							</Typography>
							{row.AttachmentText ? (
								<Typography
									variant="caption"
									color="text.secondary"
									noWrap
									sx={{ mt: 0.5, display: "block" }}
								>
									Attachment
								</Typography>
							) : null}
						</Box>
					</Tooltip>
				),
			},
		],
		[commonDateCol, theme],
	);

	const desktopColumns: ShiftLogColDef[] = useMemo(
		() => [
			...mobileColumns,
			{
				field: "TagsFlat",
				headerName: "Tags",
				flex: 1.1,
				minWidth: 100,
				sortable: true,
				renderHeader: () => (
					<Translate String="Shift.Event.Tags">Tags</Translate>
				),
				renderCell: ({ row }) => {
					const tags = Array.isArray(row.Tags)
						? row.Tags
						: Array.isArray(row.tags)
							? row.tags
							: [];

					return tags.length ? (
						<Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
							{tags.map((el, idx) => (
								<Chip
									key={idx}
									label={el}
									size="small"
									sx={sharedTableTagChipSx(theme)}
								/>
							))}
						</Box>
					) : (
						<Typography variant="caption" color="text.secondary">
							—
						</Typography>
					);
				},
			},
			{
				field: "CommentsPlain",
				headerName: "Comments",
				flex: 1.5,
				sortable: false,
				renderHeader: () => (
					<Translate String="Shift.Event.Comments">Comments</Translate>
				),
				renderCell: ({ row }) => {
					const comments = (
						Array.isArray(row.Comments)
							? row.Comments
							: Array.isArray(row.comments)
								? row.comments
								: []
					)
						.slice()
						.reverse();

					if (!comments.length) {
						return <DataGridCommentTooltip items={[]} />;
					}

					return (
						<DataGridCommentTooltip
							items={comments.map((comment: ShiftComment, idx: number) => {
								const author =
									toText(comment.CreatedBy ?? comment.createdBy) || "Unknown";
								const commentCreated = comment.Created ?? comment.created;
								const when = commentCreated
									? dayjs
											.utc(commentCreated)
											.tz(timezone)
											.format("DD/MM/YY HH:mm:ss")
									: "Unknown";

								return (
									<Box key={idx}>
										<Typography
											variant="caption"
											color="text.secondary"
											sx={{ display: "block", fontWeight: 700, mb: 0.5 }}
										>
											{author}
											{when !== "Unknown" ? ` • ${when}` : ""}
										</Typography>
										<Box
											sx={{
												fontSize: theme.typography.body2.fontSize,
												lineHeight: 1.45,
												"& p": { m: 0 },
												"& p + p": { mt: 0.5 },
												"& *": { maxWidth: "100%" },
												"& a": {
													wordBreak: "break-word",
													overflowWrap: "anywhere",
												},
											}}
										>
											<SATOSContentRender
												rehypePlugins={[rehypeRaw]}
												content={toText(comment.Text ?? comment.text)}
											/>
										</Box>
									</Box>
								);
							})}
						/>
					);
				},
			},
			{
				field: "ShiftName",
				headerName: "Shift Rotation",
				flex: 1.2,
				minWidth: 100,
				sortable: true,
				renderHeader: () => (
					<Translate String="Shift.Event.ShiftRotation">
						Shift Rotation
					</Translate>
				),
				renderCell: ({ row }) => {
					const dayNightName = row.DayNight?.Name;
					if (!dayNightName) {
						return (
							<Typography variant="caption" color="text.secondary">
								—
							</Typography>
						);
					}
					return (
						<ShiftMetaBadge
							label={dayNightName}
							color={row.DayNight?.Color ?? row.dayNight?.Color}
						/>
					);
				},
				sortComparator: (a, b) =>
					String(a || "").localeCompare(String(b || "")),
			},
			{
				field: "ShiftCrewText",
				headerName: "Crew Name",
				flex: 1.2,
				minWidth: 100,
				sortable: true,
				renderHeader: () => (
					<Translate String="Shift.Event.CrewName">Crew Name</Translate>
				),
				renderCell: ({ row }) => {
					const shiftCrewName = row.ShiftCrewText;
					if (!shiftCrewName) {
						return (
							<Typography variant="caption" color="text.secondary">
								—
							</Typography>
						);
					}
					return (
						<ShiftMetaBadge
							label={shiftCrewName}
							color={row.ShiftCrew?.Color ?? row.shiftCrew?.Color}
						/>
					);
				},
				sortComparator: (a, b) =>
					String(a || "").localeCompare(String(b || "")),
			},
			{
				field: "SystemName",
				headerName: "Systems",
				flex: 1.2,
				minWidth: 100,
				sortable: true,
				renderHeader: () => (
					<Translate String="Shift.Event.Systems">Systems</Translate>
				),
				renderCell: ({ row }) => {
					const systemsArr = toEntityLabels(
						row.Systems ?? row.systems ?? row["Systems"],
					);
					const fallbackSystemName = toText(row.SystemName);
					const fallbackSystem = toEntityLabel(row.System);
					const fallbackSystemId = toText(row.systemId ?? row.SystemId);
					const visibleSystems = systemsArr.length
						? systemsArr
						: [fallbackSystemName || fallbackSystem || fallbackSystemId].filter(
								Boolean,
							);

					if (!visibleSystems.length) {
						return (
							<Typography variant="caption" color="text.secondary">
								—
							</Typography>
						);
					}

					return (
						<Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
							{visibleSystems.map((s, i) => (
								<Chip
									key={`${s}-${i}`}
									label={s}
									size="small"
									sx={{
										height: 22,
										borderRadius: 999,
										fontWeight: 700,
										border: "1px solid",
										borderColor: alpha(theme.palette.info.main, 0.18),
										bgcolor: alpha(theme.palette.info.main, 0.08),
										color: "info.dark",
										"& .MuiChip-label": { px: 1.1 },
										maxWidth: 160,
									}}
								/>
							))}
						</Box>
					);
				},
				sortComparator: (a, b) =>
					String(a ?? "").localeCompare(String(b ?? "")),
			},
			{
				field: "GroupText",
				headerName: "Groups",
				flex: 1.2,
				minWidth: 100,
				sortable: true,
				renderHeader: () => (
					<Translate String="Shift.Event.Groups">Groups</Translate>
				),
				renderCell: ({ row }) => {
					const groupsArr = toEntityLabels(
						row.Groups ?? row.groups ?? row["Groups"],
					);
					const fallbackGroupText = toText(row.GroupText);
					const fallbackGroup = toEntityLabel(row.Group);
					const fallbackGroupId = toText(row.GroupId ?? row.groupId);
					const visibleGroups = groupsArr.length
						? groupsArr
						: [fallbackGroupText || fallbackGroup || fallbackGroupId].filter(
								Boolean,
							);

					if (!visibleGroups.length) {
						return (
							<Typography variant="caption" color="text.secondary">
								—
							</Typography>
						);
					}

					return (
						<Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
							{visibleGroups.map((g, i) => (
								<Chip
									key={`${g}-${i}`}
									label={g}
									size="small"
									sx={{
										height: 22,
										borderRadius: 999,
										fontWeight: 700,
										border: "1px solid",
										borderColor: alpha(theme.palette.primary.main, 0.18),
										bgcolor: alpha(theme.palette.primary.main, 0.08),
										color: theme.palette.primary.main,
										"& .MuiChip-label": { px: 1.1 },
										maxWidth: 160,
									}}
								/>
							))}
						</Box>
					);
				},
				// sort by the joined list
				sortComparator: (a, b) =>
					String((a ?? "") + "").localeCompare(String((b ?? "") + "")),
			},

			{
				field: "AttachmentText",
				headerName: "Attachment",
				flex: 1,
				minWidth: 120,
				sortable: false,
				renderHeader: () => (
					<Translate String="Shift.Event.Attachment">Attachment</Translate>
				),
				renderCell: ({ row }) => {
					const filepath = toText(row.Filepath ?? row.filepath);
					const filename = toText(row.Filename ?? row.filename) || "Attachment";

					return filepath ? (
						<MLink
							href={filepath}
							target={
								isImageAttachment({ Url: filepath, FileName: filename })
									? undefined
									: "_blank"
							}
							rel="noopener noreferrer"
							underline="hover"
							onClick={(event) => {
								if (!isImageAttachment({ Url: filepath, FileName: filename }))
									return;
								event.preventDefault();
								setImageAttachmentPreview({
									url: filepath,
									fileName: filename,
								});
							}}
							sx={{
								display: "inline-flex",
								alignItems: "center",
								gap: 0.75,
								fontWeight: 500,
								color: "primary.main",
							}}
						>
							<InsertDriveFileOutlinedIcon sx={{ fontSize: 18 }} />
							<Typography variant="body2" noWrap sx={{ maxWidth: 160 }}>
								{filename}
							</Typography>
							<ArrowOutwardIcon sx={{ fontSize: 14, opacity: 0.7 }} />
						</MLink>
					) : (
						<Typography variant="caption" color="text.secondary">
							—
						</Typography>
					);
				},
			},
			{
				field: "IsOutOfServiceText",
				headerName: "Out of service",
				type: "string",
				flex: 0.7,
				minWidth: 70,
				align: "center",
				headerAlign: "center",
				editable: true,
				renderHeader: () => (
					<Translate String="Shift.Event.OutOfService">
						Out of service
					</Translate>
				),
				renderCell: (p) => (
					<Box sx={{ display: "flex", justifyContent: "center" }}>
						<Checkbox
							checked={p.row.IsOutOfServiceBool}
							disabled
							size="small"
							sx={sharedTableCheckboxSx(theme)}
						/>
					</Box>
				),
			},
			{
				field: "IsHandoverEventText",
				headerName: "Handover",
				type: "string",
				flex: 0.6,
				minWidth: 70,
				align: "center",
				headerAlign: "center",
				editable: true,
				renderHeader: () => (
					<Translate String="Shift.Event.Handover">Handover</Translate>
				),
				renderCell: (p) => (
					<Box sx={{ display: "flex", justifyContent: "center" }}>
						<Checkbox
							checked={p.row.IsHandoverEventBool}
							disabled
							size="small"
							sx={sharedTableCheckboxSx(theme)}
						/>
					</Box>
				),
			},
		],
		[mobileColumns, theme, timezone],
	);

	const columns: ShiftLogColDef[] = isMobile ? mobileColumns : desktopColumns;
	const exportColumns = useMemo(
		() =>
			(columns ?? []).filter((col) => {
				const vis = columnVisibilityModel[String(col.field)];
				return vis !== false; // undefined or true -> visible
			}),
		[columns, columnVisibilityModel],
	);
	const anyExportColumns = useMemo<AnyCol[]>(
		() =>
			exportColumns.map((col) => ({
				field: String(col.field),
				headerName:
					typeof col.headerName === "string"
						? col.headerName
						: String(col.field),
			})),
		[exportColumns],
	);
	const pdfMeta = useMemo<PdfMeta>(
		() => ({
			title: "Shift Log",
			subtitle:
				selectedGroup === "all"
					? "All Groups"
					: (shiftOptions?.Groups?.find((g) => String(g.Id) === selectedGroup)
							?.Name ?? ""),
			url: window.location.href,
			logoUrl: logoSvg,
			footerText: "Satos",
			dateFormat: "DD/MM/YY HH:mm:ss",
		}),
		[selectedGroup, shiftOptions?.Groups],
	);
	const pdfColumns = useMemo(() => anyExportColumns, [anyExportColumns]);

	const openPdfExportDialog = useCallback(() => {
		setSelectedPdfFields(new Set(pdfColumns.map((col) => col.field)));
		setPdfExportOpts((prev) => ({
			...prev,
			dateFormat: pdfMeta.dateFormat ?? prev.dateFormat,
			footerText: pdfMeta.footerText ?? prev.footerText,
			truncateAt: pdfMeta.truncateAt ?? prev.truncateAt,
			pageSize: pdfMeta.pageSize ?? prev.pageSize,
		}));
		setPdfDialogOpen(true);
	}, [pdfColumns, pdfMeta]);

	const togglePdfColumn = useCallback((field: string) => {
		setSelectedPdfFields((prev) => {
			const next = new Set(prev);
			if (next.has(field)) next.delete(field);
			else next.add(field);
			return next;
		});
	}, []);

	const selectAllPdfColumns = useCallback(() => {
		setSelectedPdfFields(new Set(pdfColumns.map((col) => col.field)));
	}, [pdfColumns]);

	const deselectAllPdfColumns = useCallback(() => {
		setSelectedPdfFields(new Set());
	}, []);

	const handlePdfExport = useCallback(async () => {
		const selectedCols = pdfColumns.filter((col) =>
			selectedPdfFields.has(col.field),
		);
		if (selectedCols.length === 0) return;

		setIsPdfExporting(true);
		try {
			await buildPdfShowAllInfo(
				exportableRows,
				anyExportColumns,
				exportFileName,
				pdfMeta,
				{
					selectedColumns: selectedCols,
					dateFormat: pdfExportOpts.dateFormat,
					footerText: pdfExportOpts.footerText || "Satos",
					truncateAt: pdfExportOpts.truncateEnabled
						? pdfExportOpts.truncateAt || 200
						: undefined,
					pageSize: pdfExportOpts.pageSize,
					groupBy: pdfExportOpts.groupBy || undefined,
				},
			);
			setPdfDialogOpen(false);
		} catch (e) {
			console.error("PDF export failed:", e);
		} finally {
			setIsPdfExporting(false);
		}
	}, [
		anyExportColumns,
		exportFileName,
		exportableRows,
		pdfColumns,
		pdfExportOpts,
		pdfMeta,
		selectedPdfFields,
	]);

	useEffect(() => {
		const actions = (
			<>
				<MenuItem
					onClick={() => handleSaveAndClear(groupIdOrUndefined)}
					disabled={isUpdatingSaveClear || !groupIdOrUndefined}
				>
					{isUpdatingSaveClear && (
						<ListItemIcon>
							<CircularProgress size={16} />
						</ListItemIcon>
					)}
					<Translate String="ShiftLog.SaveClear">Save and clear</Translate>
				</MenuItem>
				{appConfig?.Features.Ai && (
					<MenuItem onClick={handleOpenSummarizeDialogue}>
						<Translate String="ShiftLog.Summarize">Summarize</Translate>
					</MenuItem>
				)}
				{!isMobile && (
					<MenuItem onClick={handleOpenColumnsPanel}>
						<ListItemIcon>
							<ViewWeekRoundedIcon fontSize="small" />
						</ListItemIcon>
						<ListItemText primary={GetLanguageValueAsString(translation, "Auto.Features.Shift.Pages.ShiftLog.L1302C29.Columns", "Columns")} />
					</MenuItem>
				)}
				{!isMobile && (
					<MenuItem onClick={handleOpenFilterPanel}>
						<ListItemIcon>
							<FilterAltRoundedIcon fontSize="small" />
						</ListItemIcon>
						<ListItemText primary={GetLanguageValueAsString(translation, "Auto.Features.Shift.Pages.ShiftLog.L1310C29.Filters", "Filters")} />
					</MenuItem>
				)}
				<MenuItem
					onClick={handleExportCsv}
					disabled={isMobile || exportableRows.length === 0}
				>
					<ListItemIcon>
						<TableViewOutlinedIcon fontSize="small" />
					</ListItemIcon>
					<ListItemText primary={GetLanguageValueAsString(translation, "Auto.Features.Shift.Pages.ShiftLog.L1320C28.ExportCSV", "Export CSV")} />
				</MenuItem>
				<MenuItem
					onClick={() => {
						void handleExportExcel();
					}}
					disabled={isMobile || exportableRows.length === 0 || isExcelExporting}
				>
					<ListItemIcon>
						{isExcelExporting ? (
							<CircularProgress size={16} />
						) : (
							<TableChartIcon fontSize="small" />
						)}
					</ListItemIcon>
					<ListItemText
						primary={isExcelExporting ? "Exporting Excel..." : "Export Excel"}
					/>
				</MenuItem>
				<MenuItem
					onClick={openPdfExportDialog}
					disabled={exportableRows.length === 0}
				>
					<ListItemIcon>
						<PictureAsPdfOutlinedIcon fontSize="small" />
					</ListItemIcon>
					<ListItemText primary={GetLanguageValueAsString(translation, "Auto.Features.Shift.Pages.ShiftLog.L1346C28.ExportPDF", "Export PDF")} />
				</MenuItem>
			</>
		);
		setHeaderActions(actions);
		return () => setHeaderActions(null);
	}, [
		appConfig?.Features.Ai,
		exportableRows,
		groupIdOrUndefined,
		handleExportCsv,
		handleExportExcel,
		handleOpenColumnsPanel,
		handleOpenFilterPanel,
		handleSaveAndClear,
		handleOpenSummarizeDialogue,
		isUpdatingSaveClear,
		isExcelExporting,
		isMobile,
		openPdfExportDialog,
		setHeaderActions,
	]);
	useEffect(() => {
		const allowed = new Set((columns ?? []).map((c) => c.field));
		const pruned: GridColumnVisibilityModel = {};
		let changed = false;

		Object.entries(columnVisibilityModel).forEach(([field, visible]) => {
			if (allowed.has(field)) {
				pruned[field] = visible;
			} else {
				changed = true;
			}
		});

		if (changed) setColumnVisibilityModel(pruned);
	}, [columns]);
	const cardBackground =
		toText(COL.cardBg(theme) as unknown) || "background.paper";
	if (optionsLoading || loadingAppConfig || !appConfig) {
		return (
			<Box
				display="flex"
				alignItems="center"
				justifyContent="center"
				height="100%"
			>
				<CircularProgress />
				<Box ml={2}>
					<Translate String="ShiftLog.LoadingShiftGroups">
						Loading shift groups...
					</Translate>
				</Box>
			</Box>
		);
	}

	if (!shiftOptions?.Groups?.length) {
		return (
			<Typography variant="body1">
				<Translate String="Shift.Event.NoGroupsAvailable">
					No shift groups available
				</Translate>
			</Typography>
		);
	}
	return (
		<Card
			elevation={0}
			sx={{
				borderRadius: 2,
				border: (t) => `1px solid ${t.palette.divider}`,
				bgcolor: cardBackground,
				height: "100%",
				minHeight: 0,
				display: "flex",
				flexDirection: "column",
				m: { xs: 1, sm: 1.5, md: 2 },
				overflow: "hidden",
			}}
		>
			<TableContainer
				sx={{
					width: "100%",
					margin: 0,
					mt: 0,
					overflow: "hidden",
					display: "flex",
					flexDirection: "column",
				}}
			>
				<Card
					variant="outlined"
					sx={{
						flex: 1,
						minHeight: 0,
						borderRadius: 2,
						overflow: "hidden",
						display: "flex",
						flexDirection: "column",
						mb: 1.5,
					}}
				>
					<Box
						sx={sharedShiftDataGridShellSx(theme, {
							includeInnerScrollbar: true,
						})}
					>
						{isMobile ? (
							<Box
								sx={{
									flex: 1,
									minHeight: 0,
									display: "flex",
									flexDirection: "column",
									overflow: "hidden",
								}}
							>
								{shiftLogLoading || isUpdating ? (
									<Box
										sx={{
											flex: 1,
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											minHeight: 120,
										}}
									>
										<CircularProgress />
									</Box>
								) : exportableRows.length === 0 ? (
									<CustomNoRowsOverlay />
								) : (
									<MobileShiftEventCards
										rows={exportableRows}
										timezone={timezone}
										onRowClick={handleClickOpenShiftEvent}
									/>
								)}
							</Box>
						) : (
							<StyledDataGrid
								apiRef={apiRef}
								sx={{
									flex: 1,
									minHeight: 0,
									minWidth: "100%",
									height: exportableRows.length === 0 ? 240 : "100%",
									border: "none",
								}}
								getRowClassName={(
									params: GridRowClassNameParams<ShiftLogRow>,
								) => {
									const value = params.row.ShiftName || "";
									// safe class name
									const slug = value
										.toString()
										.replace(/\s+/g, "-")
										.toLowerCase();
									const shiftClass = value ? `shift-row-${slug}` : "";
									const index = params.indexRelativeToCurrentPage ?? 0;
									const stripeClass = index % 2 === 0 ? "row-even" : "row-odd";
									return [shiftClass, stripeClass].filter(Boolean).join(" ");
								}}
								rows={exportableRows}
								showToolbar
								columns={columns}
								rowCount={totalItems}
								hideFooterPagination
								columnVisibilityModel={columnVisibilityModel}
								onColumnVisibilityModelChange={(model) =>
									setColumnVisibilityModel(model)
								}
								disableRowSelectionOnClick
								slots={{
									toolbar: CustomToolbarV2 as React.JSXElementConstructor<
										Record<string, unknown>
									>,
									noRowsOverlay: CustomNoRowsOverlay,
									booleanCellTrueIcon: () => (
										<Checkbox
											checked
											disabled
											size="small"
											sx={sharedTableCheckboxSx(theme)}
										/>
									),
									booleanCellFalseIcon: () => (
										<Checkbox
											checked={false}
											disabled
											size="small"
											sx={sharedTableCheckboxSx(theme)}
										/>
									),
								}}
								slotProps={{
									toolbar: {
										hidden: true,
										rows: exportableRows,
										columns: exportColumns,
										fileName: exportFileName,
										hideQuickFilter: true,
										showPdfOptions: true,
										pdfMeta,
									} as Record<string, unknown>,
								}}
								getRowId={(row: ShiftLogRow) =>
									resolveEventId(row) ?? row.DateText
								}
								onCellClick={handleCellClick}
								getRowHeight={() => "auto"}
								disableDensitySelector={isMobile}
								processRowUpdate={processRowUpdate}
								onProcessRowUpdateError={(err) => console.error(err)}
								loading={shiftLogLoading || isUpdating}
							/>
						)}
					</Box>
				</Card>

				{/* MUI 5 TablePagination */}
				{exportableRows.length > 0 && (
					<TablePagination
						component="div"
						count={totalItems}
						page={page - 1}
						onPageChange={(_e, newPage) => setPage(newPage + 1)}
						rowsPerPage={pageSize}
						onRowsPerPageChange={handleRowsPerPageChange}
						rowsPerPageOptions={[10, 25, 50, 100]}
						SelectProps={{ size: "small" }}
						labelRowsPerPage={
							<Translate String="Shift.PaginationRows">
								Rows per page:
							</Translate>
						}
						labelDisplayedRows={({ from, to, count }) =>
							count !== -1 ? `${from}–${to} of ${count}` : `${from}–${to}`
						}
						showFirstButton={!isMobile}
						showLastButton={!isMobile}
						sx={{
							borderTop: (t) => `1px solid ${t.palette.divider}`,
							bgcolor: bandBg(theme),
							minHeight: 28,
							"& .MuiTablePagination-toolbar": {
								minHeight: 28,
								px: 1.5,
								py: 0,
							},
							"& .MuiTablePagination-selectLabel": {
								mr: 0.5,
								display: { xs: "none", sm: "block" },
							},
							"& .MuiTablePagination-displayedRows": {
								fontSize: { xs: "0.75rem", sm: "inherit" },
							},
							"& .MuiTablePagination-select": {
								py: 0,
								px: 0.75,
								minHeight: 22,
							},
							"& .MuiTablePagination-actions": { ml: 0 },
							"& .MuiIconButton-root": {
								padding: 2,
								"& svg": { fontSize: 16 },
							},
						}}
					/>
				)}
			</TableContainer>

			<ShiftEvent
				page="ShiftLog"
				open={openShiftEvent}
				onClose={handleCloseShiftEvent}
				shiftEventData={shiftEventData}
				selectedGroup={selectedGroup}
			/>
			<ImageAttachmentPreviewDialog
				preview={imageAttachmentPreview}
				onClose={() => setImageAttachmentPreview(null)}
			/>

			{/* AI summarize */}
			<ShiftSummaryDialogue
				open={openSummarizeDialogue}
				handleClose={setOpenSummarizeDialogue}
				groupId={selectedGroup === "all" ? 0 : Number(selectedGroup)}
			/>

			<PdfExportDialog
				open={pdfDialogOpen}
				onClose={() => !isPdfExporting && setPdfDialogOpen(false)}
				isExporting={isPdfExporting}
				cols={pdfColumns}
				headerOf={headerOf}
				selectedFields={selectedPdfFields}
				toggleColumn={togglePdfColumn}
				selectAllColumns={selectAllPdfColumns}
				deselectAllColumns={deselectAllPdfColumns}
				exportOpts={pdfExportOpts}
				setExportOpts={setPdfExportOpts}
				onExport={() => {
					void handlePdfExport();
				}}
				canExport={selectedPdfFields.size > 0}
			/>
		</Card>
	);
};

export default ShiftLog;
